import { Box, TextField, Checkbox, FormControlLabel, Button, Tooltip, Avatar, CircularProgress } from '@mui/material';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useLazyGetSwarmsMetricsQuery } from '../../../../services/swarmsApi';
import { useLazyGetSwarmsQuery } from '../../../../services/swarmsApi';
import { useLazyGetBuyManagersByIdQuery } from '../../../../services/buyManagersApi';
import { eventDateGridFormatter } from '../../../../utils/dates';
import { useSearchParams } from './hooks/useSearchParams';
import { useSwarmRows } from './hooks/useSwarmRows';
import { useKeyboardSearch } from './hooks/useKeyboardSearch';
import { useEffect, useState } from 'react';

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  '& .MuiTextField-root': {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));



const SwarmsHistory = () => {
  const [getMetrics, { data: apiResponse, isLoading: isLoadingMetrics }] = useLazyGetSwarmsMetricsQuery();
  const [getSwarms, { isLoading: isLoadingSwarms }] = useLazyGetSwarmsQuery();
  const [getBuyManagers, { isLoading: isLoadingBuyManagers }] = useLazyGetBuyManagersByIdQuery();

  const { searchParams, updateSearchParams, createQueryParams } = useSearchParams();
  const { rows } = useSwarmRows(apiResponse, getSwarms, getBuyManagers);
  const [isLoading, setIsLoading] = useState(false);

  const apiRef = useGridApiRef();
  const [groupByEvent, setGroupByEvent] = useState(false);

  useEffect(()=> {
    setIsLoading(isLoadingMetrics || isLoadingSwarms || isLoadingBuyManagers);
  },[isLoadingMetrics, isLoadingSwarms, isLoadingBuyManagers]);

  console.log('isLoading', isLoading);

  const handleSearch = async (params) => {
    try{
      setIsLoading(true);
      const queryParams = createQueryParams(params);
      await getMetrics(queryParams).unwrap();
    }catch(error){
      if (error.status == 401) return; //error managed on baseQueryWithReauth
      throw error
    }finally{
      setIsLoading(false);
    }
  };

  useKeyboardSearch(handleSearch);

  // Initial search
  useEffect(() => {
    handleSearch();
  }, []);

  const columns = [
    {
      headerName: "BUY MANAGER",
      field: "buyManager",
      flex:0,
      aggregable: false,
      align:"center",
      renderCell: (params) => {
        if(!params.value) return
        const avatar = {color: params.value?.color ||'#FFB74D', initials: Array.from(params.value?.firstName[0]) + Array.from(params.value?.lastName)[0]}
        return (
          <Tooltip title={`${params.value?.firstName} ${params.value?.lastName}`}>
            <Avatar sx={{ backgroundColor: avatar?.color || '#FFB74D', height:"30px", width:"30px", fontSize:"12px", fontWeight:"700" }}>
              {avatar.initials}
            </Avatar>
          </Tooltip>
        );
      }
    },
    { 
      field: 'eventName', 
      headerName: 'EVENT', 
      width: 250,
      renderCell: (params) => {
        if (params?.rowNode?.type === 'group') {
          return (
            <strong title={params.value}>{params.value}</strong>
          );
        }
        if (params?.rowNode?.type === 'pinnedRow') {
          return (
            <span style={{ color: 'var(--orange)', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase' }}>
              Request Total Summary
            </span>
          );
        }
        return params.value || '';
      }
    },
    { 
      field: 'venueName', 
      headerName: 'VENUE', 
      flex:3
    },
    { 
      field: 'date', 
      headerName: 'EVENT DATE', 
      flex:2,
      valueGetter: (params) => params.row.event?.date,
      valueFormatter: (params) => params.value && !isNaN(new Date(params.value).getTime()) ? eventDateGridFormatter(params.value) : ''
    },
    { 
      field: 'salesDate', 
      headerName: 'SALE DATE', 
      flex:2,
      valueFormatter: (params) => params.value && eventDateGridFormatter(params.value + 'Z')
    },
    { 
      field: 'completed', 
      headerName: 'PURCHASED', 
      type: 'number',
      flex:1,
      align: 'center',
    },
    { 
      field: 'rejected', 
      headerName: 'PASSED', 
      type: 'number',
      flex:1,
      align: 'center',
    },
    { 
      field: 'lost', 
      headerName: 'LOST', 
      type: 'number',
      flex:1,
      align: 'center',
    },
  ];

  useEffect(() => {
    const storedGroupBy = sessionStorage.getItem('groupByEvent');
    if (storedGroupBy) {
      setGroupByEvent(storedGroupBy === 'true');
    }
  }, []);

  const handleGroupByChange = (e) => {
    const checked = e.target.checked;
    setGroupByEvent(checked);
    sessionStorage.setItem('groupByEvent', checked.toString());
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ 
        height: '100%', 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column'
      }}>
        <StyledBox>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              placeholder='Search Event Name...'
              style={{ margin: '0px', minWidth: 245, width: '25%' }}
              value={searchParams.eventName}
              onChange={(e) => updateSearchParams({ ...searchParams, eventName: e.target.value })}
            />

            <TextField
              placeholder="Search Venue Name..."
              style={{ margin: '0px', minWidth: 245, width: '25%' }}
              value={searchParams.venueName}
              onChange={(e) => updateSearchParams({ ...searchParams, venueName: e.target.value })}
            />

            <DatePicker
              value={searchParams.selectedDate}
              onChange={(newValue) => updateSearchParams({ ...searchParams, selectedDate: newValue })}
              margin={0}
              slotProps={{
                textField: {
                  style: {
                    margin: '0px'
                  },
                  placeholder: 'Search Swarm Date...'
                },
              actionBar: { actions: ['today', 'clear'] }
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={groupByEvent} 
                  onChange={handleGroupByChange}
                />
              }
              label="Group by Event"
            />
            <Button 
              variant="contained" 
              onClick={()=>handleSearch()}
              disabled={isLoading}
              sx={{ minWidth: 100 }}
            >
              Search
            </Button>
          </Box>
        </StyledBox>
        
        <Box sx={{ flex: 1, width: '100%', height: '65vh' }}>
          <DataGridPremium
            apiRef={apiRef}
            rows={rows}
            columns={columns}
            rowHeight={36}
            headerHeight={40}
            paginationMode='server'
            rowCount={apiResponse?.totalCount || 0}
            paginationModel={{
              pageSize: searchParams.RecordsLimit,
              page: searchParams.PageNo - 1
            }}
            onPaginationModelChange={(model) => {
              let newParams = {
                ...searchParams,
                PageNo: model.page + 1,
                RecordsLimit: model.pageSize
              }
              updateSearchParams(newParams);
              handleSearch(newParams);
            }}
            pagination
            disableRowSelectionOnClick
            getRowId={(row) => row.id || Math.random()}
            loading={isLoading}
            localeText={{
              noRowsLabel: 'No results found',
            }}
            hideFooter
            disableColumnPinning
            checkboxSelection={false}
            rowGroupingModel={groupByEvent ? ['eventName'] : []}
            initialState={{
              aggregation: {
                model: {
                  completed: 'sum',
                  rejected: 'sum',
                  lost: 'sum',
                  accepted: 'sum'
                }
              }
            }}
            sx={{
              height: '65vh',
              width: '100%',
              '& .MuiDataGrid-cell': {
                whiteSpace: 'normal',
                lineHeight: 'normal',
              },
              '& .MuiDataGrid-aggregationColumnHeaderLabel': {
                display: 'none',
              },
              paddingBottom: '1em',
              zIndex: 0
            }}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default SwarmsHistory;